import { validateInitData } from "../auth.js";

const MAX_RETRY_DELAY_MS = 60_000;
const AUTH_RELOAD_THROTTLE_MS = 15_000;

export function createTelegramAuthManager({
  windowRef = window,
  showOverlay,
  hideOverlay,
  safeFetch,
  resolveApiUrl,
  getTelegramWebApp,
  setTelegramWebApp,
  getCurrentUser,
  setCurrentUser,
  getInitData,
  setInitData,
  isAuthInProgress,
  setAuthInProgress,
  isAuthCompleted,
  setAuthCompleted,
  getAttemptCount,
  setAttemptCount,
  getLastReloadAt,
  setLastReloadAt,
  onAuthSuccess = () => {},
}) {
  if (typeof showOverlay !== "function") {
    throw new Error("showOverlay callback is required");
  }
  if (typeof hideOverlay !== "function") {
    throw new Error("hideOverlay callback is required");
  }
  if (typeof safeFetch !== "function") {
    throw new Error("safeFetch callback is required");
  }
  const timers = {
    retryTimeoutId: null,
  };

  function getApiUrl() {
    return typeof resolveApiUrl === "function" ? resolveApiUrl() : resolveApiUrl;
  }

  function getAttempts() {
    return typeof getAttemptCount === "function"
      ? getAttemptCount()
      : 0;
  }

  function setAttempts(value) {
    if (typeof setAttemptCount === "function") {
      setAttemptCount(value);
    }
  }

  function updateInitData(value) {
    if (typeof setInitData === "function") {
      setInitData(value);
    }
  }

  function setTelegramApp(app) {
    if (typeof setTelegramWebApp === "function") {
      setTelegramWebApp(app);
    }
  }

  function getTelegramApp() {
    const explicit = typeof getTelegramWebApp === "function"
      ? getTelegramWebApp()
      : null;
    if (explicit) {
      return explicit;
    }
    const candidate = windowRef.Telegram?.WebApp;
    if (candidate && typeof setTelegramWebApp === "function") {
      setTelegramWebApp(candidate);
    }
    return candidate || explicit;
  }

  // Кэш для предотвращения множественных проверок
  let lastInitDataCheck = null;
  let lastInitDataCheckTime = 0;
  const INIT_DATA_CHECK_THROTTLE_MS = 2000; // Проверяем не чаще раза в 2 секунды
  
  function selectCurrentInitData() {
    // Если авторизация уже завершена, не проверяем initData повторно
    if (isCompleted()) {
      return lastInitDataCheck || null;
    }
    
    // Throttle проверки initData - не логируем слишком часто
    const now = Date.now();
    const shouldLog = !lastInitDataCheck || (now - lastInitDataCheckTime) >= INIT_DATA_CHECK_THROTTLE_MS;
    
    const telegram = getTelegramApp();
    
    // Пробуем получить initData из URL параметров (tgWebAppData)
    // Согласно документации: https://docs.telegram-mini-apps.com/platform/init-data
    let urlInitData = null;
    try {
      const urlParams = new URLSearchParams(windowRef.location.search);
      urlInitData = urlParams.get('tgWebAppData') || urlParams.get('initData');
      if (urlInitData) {
        console.log("[AUTH] 🔍 Found initData in URL params", {
          length: urlInitData.length,
          preview: urlInitData.substring(0, 100) + "..."
        });
      }
    } catch (e) {
      console.error("[AUTH] ❌ Error parsing URL params:", e);
    }
    
    // Детальная проверка всех источников
    const webAppInitData = windowRef.Telegram?.WebApp?.initData;
    const webAppInitDataUnsafe = windowRef.Telegram?.WebApp?.initDataUnsafe;
    const telegramInitData = telegram?.initData;
    const storedInitData = typeof getInitData === "function" ? getInitData() : null;
    
    // Логируем только если прошло достаточно времени с последней проверки
    if (shouldLog) {
      console.log("[AUTH] 🔍 Detailed initData check:", {
      hasTelegram: !!telegram,
      hasWindowTelegram: !!windowRef.Telegram,
      hasWebApp: !!(windowRef.Telegram?.WebApp),
      webAppInitData: {
        exists: !!webAppInitData,
        type: typeof webAppInitData,
        length: webAppInitData?.length || 0,
        hasHash: webAppInitData?.includes("hash=") || false,
        preview: webAppInitData ? webAppInitData.substring(0, 100) + "..." : null
      },
      webAppInitDataUnsafe: {
        exists: !!webAppInitDataUnsafe,
        type: typeof webAppInitDataUnsafe,
        isObject: typeof webAppInitDataUnsafe === 'object',
        keys: typeof webAppInitDataUnsafe === 'object' && webAppInitDataUnsafe !== null ? Object.keys(webAppInitDataUnsafe) : null
      },
      telegramInitData: {
        exists: !!telegramInitData,
        type: typeof telegramInitData,
        length: telegramInitData?.length || 0
      },
      urlInitData: {
        exists: !!urlInitData,
        length: urlInitData?.length || 0
      },
      storedInitData: {
        exists: !!storedInitData,
        type: typeof storedInitData,
        length: storedInitData?.length || 0
      },
      locationHref: windowRef.location?.href?.substring(0, 200) || 'none',
      locationSearch: windowRef.location?.search || 'none',
      ts: new Date().toISOString()
      });
    }
    
    const candidates = [
      storedInitData,
      urlInitData,
      telegramInitData,
      webAppInitData,
      // initDataUnsafe - это объект, не строка, поэтому не используем его напрямую
    ];
    
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      const source = i === 0 ? 'storedInitData' : 
                     i === 1 ? 'URL params (tgWebAppData)' :
                     i === 2 ? 'telegram.initData' :
                     'window.Telegram.WebApp.initData';
      
      if (typeof candidate === "string" && candidate.includes("hash=")) {
        if (shouldLog) {
          console.log("[AUTH] ✅ Found valid initData from:", source, {
            length: candidate.length,
            hasHash: candidate.includes("hash="),
            hasAuthDate: candidate.includes("auth_date="),
            preview: candidate.substring(0, 100) + "..."
          });
        }
        // Обновляем кэш
        lastInitDataCheck = candidate;
        lastInitDataCheckTime = Date.now();
        return candidate;
      }
    }
    
    // Если initDataUnsafe - это объект, попробуем преобразовать его в строку
    if (webAppInitDataUnsafe && typeof webAppInitDataUnsafe === 'object') {
      console.log("[AUTH] ⚠️ initDataUnsafe is object, not string. This is expected - use initData instead.");
    }
    
    console.error("[AUTH] ❌ No valid initData found in any source!");
    console.error("[AUTH] ❌ This means Telegram WebApp did not provide initData.");
    console.error("[AUTH] ❌ Possible reasons:");
    console.error("[AUTH] ❌ 1. App not opened through bot");
    console.error("[AUTH] ❌ 2. Bot settings in BotFather - Mini App not configured");
    console.error("[AUTH] ❌ 3. Opening app in regular browser instead of Telegram");
    
    return null;
  }

  // Флаг для отслеживания отказа в доступе
  let accessDenied = false;
  
  function setAccessDenied(value) {
    accessDenied = value;
  }
  
  function isAccessDenied() {
    return accessDenied;
  }
  
  function buildAuthHeaders() {
    // ВАЖНО: Если доступ разрешен глобально, игнорируем локальный флаг accessDenied
    // Это позволяет пользователям с доступом продолжать использовать API
    if (window.__accessGranted && !window.__accessDenied) {
      // Пользователь имеет доступ - разрешаем запросы
      const initData = selectCurrentInitData();
      if (!initData || typeof initData !== "string") {
        // Логируем только в режиме отладки
        if (localStorage.getItem('debug') === 'true') {
          console.warn("[AUTH] No initData available (but access granted)");
        }
        return {};
      }
      
      const headers = {
        "X-Telegram-Init-Data": encodeURIComponent(initData),
      };
      // Логируем только в режиме отладки
      if (localStorage.getItem('debug') === 'true') {
        console.log("[AUTH] Building auth headers (access granted)");
      }
      return headers;
    }
    
    // Если доступ был отклонен, не отправляем заголовки авторизации
    // Это предотвратит дальнейшие запросы к API
    if (accessDenied || window.__accessDenied) {
      // Логируем только в режиме отладки
      if (localStorage.getItem('debug') === 'true') {
        console.warn("[AUTH] Access denied - blocking API requests");
      }
      return null; // Возвращаем null, чтобы вызывающий код мог проверить
    }
    
    // Проверяем, что доступ разрешен
    if (!window.__accessGranted) {
      // Логируем только в режиме отладки
      if (localStorage.getItem('debug') === 'true') {
        console.warn("[AUTH] Access not granted yet - may block request");
      }
    }
    
    const initData = selectCurrentInitData();
    if (!initData || typeof initData !== "string") {
      // Логируем только в режиме отладки
      if (localStorage.getItem('debug') === 'true') {
        console.warn("[AUTH] No initData available");
      }
      return {};
    }
    
    const headers = {
      "X-Telegram-Init-Data": encodeURIComponent(initData),
    };
    // Логируем только в режиме отладки
    if (localStorage.getItem('debug') === 'true') {
      console.log("[AUTH] Building auth headers");
    }
    return headers;
  }

  function getLastReloadTimestamp() {
    return typeof getLastReloadAt === "function"
      ? getLastReloadAt()
      : 0;
  }

  function setLastReloadTimestamp(value) {
    if (typeof setLastReloadAt === "function") {
      setLastReloadAt(value);
    }
  }

  function handleInvalidInitData(reason = "unknown") {
    console.warn("[AUTH] handleInvalidInitData invoked", {
      reason,
      ts: new Date().toISOString(),
    });
    updateInitData(null);
    if (typeof setAuthCompleted === "function") {
      setAuthCompleted(false);
    }
    const now = Date.now();
    const lastReload = getLastReloadTimestamp();
    if (now - lastReload < AUTH_RELOAD_THROTTLE_MS) {
      console.warn("[AUTH] reload throttled, skipping immediate reload");
      return;
    }
    setLastReloadTimestamp(now);
    const telegram = getTelegramApp();
    if (telegram?.showAlert) {
      telegram.showAlert("Сессия истекла, обновляем подключение…");
    }
    const performReload = () => windowRef.location.reload();
    if (telegram?.reload) {
      windowRef.setTimeout(() => {
        try {
          telegram.reload();
        } catch (error) {
          console.error(
            "Telegram reload failed, fallback to window reload",
            error,
          );
          performReload();
        }
      }, 200);
    } else {
      windowRef.setTimeout(performReload, 200);
    }
  }

  function markAuthInProgress(value) {
    if (typeof setAuthInProgress === "function") {
      setAuthInProgress(value);
    }
  }

  function markAuthCompleted(value) {
    if (typeof setAuthCompleted === "function") {
      setAuthCompleted(value);
    }
  }

  function isCompleted() {
    return typeof isAuthCompleted === "function"
      ? isAuthCompleted()
      : false;
  }

  function isInProgress() {
    return typeof isAuthInProgress === "function"
      ? isAuthInProgress()
      : false;
  }

  function showOverlayMessage(message, showBot = true, title) {
    showOverlay(message, showBot, title);
  }

  async function initAuth() {
    // НЕ инициализируем авторизацию если доступ уже запрещен
    if (window.__accessDenied) {
      console.log("[AUTH] ⚠️ Access denied - skipping auth initialization");
      return;
    }
    console.log("[AUTH] initAuth called", {
      isAuthCompleted: isCompleted(),
      isAuthInProgress: isInProgress(),
      ts: new Date().toISOString(),
    });
    if (isCompleted() || isInProgress()) {
      return;
    }
    markAuthInProgress(true);

    const telegram = getTelegramApp();
    if (!telegram) {
      showOverlayMessage(
        "Telegram WebApp не инициализирован. Обновите мини-приложение или запустите его через бота заново.",
        true,
        "Ошибка инициализации",
      );
      markAuthInProgress(false);
      return;
    }

    // Пробуем получить initData разными способами
    const initData = selectCurrentInitData() || windowRef.Telegram?.WebApp?.initData;
    
    if (!initData) {
      const attempts = getAttempts() + 1;
      setAttempts(attempts);
      const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * attempts);
      const retryDelaySec = Math.round(retryDelayMs / 1_000);
      
      console.log("[AUTH] ❌ initData not found", {
        attempts,
        retryDelaySec,
        hasTelegram: !!windowRef.Telegram,
        hasWebApp: !!(windowRef.Telegram?.WebApp),
        hasInitData: !!(windowRef.Telegram?.WebApp?.initData),
        hasInitDataUnsafe: !!(windowRef.Telegram?.WebApp?.initDataUnsafe),
        ts: new Date().toISOString()
      });
      
      showOverlayMessage(
        `Не удалось получить данные авторизации. Запустите мини-приложение через бота Telegram.
Повтор через ${retryDelaySec} сек.`,
        true,
        "Ошибка авторизации",
      );
      windowRef.setTimeout(() => {
        if (!getCurrentUser?.() && !isInProgress()) {
          initAuth();
        }
      }, retryDelayMs);
      markAuthInProgress(false);
      return;
    }

    if (typeof initData !== "string" || initData.length < 50) {
      const attempts = getAttempts() + 1;
      setAttempts(attempts);
      const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * attempts);
      const retryDelaySec = Math.round(retryDelayMs / 1_000);
      showOverlayMessage(
        `Ошибка: некорректные данные авторизации. Обновите мини-приложение через бота.
Повтор через ${retryDelaySec} сек.`,
        true,
        "Ошибка авторизации",
      );
      windowRef.setTimeout(() => {
        if (!getCurrentUser?.() && !isInProgress()) {
          initAuth();
        }
      }, retryDelayMs);
      markAuthInProgress(false);
      return;
    }

    if (!initData.includes("hash=") || !initData.includes("auth_date=")) {
      const attempts = getAttempts() + 1;
      setAttempts(attempts);
      const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * attempts);
      const retryDelaySec = Math.round(retryDelayMs / 1_000);
      showOverlayMessage(
        `Ошибка: некорректные данные авторизации. Обновите мини-приложение через бота.
Повтор через ${retryDelaySec} сек.`,
        true,
        "Ошибка авторизации",
      );
      windowRef.setTimeout(() => {
        if (!getCurrentUser?.() && !isInProgress()) {
          initAuth();
        }
      }, retryDelayMs);
      markAuthInProgress(false);
      return;
    }

    const validation = validateInitData(initData, { windowRef });
    if (!validation.valid && validation.reason.includes("истёк")) {
      const attempts = getAttempts() + 1;
      setAttempts(attempts);
      const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * attempts);
      const retryDelaySec = Math.round(retryDelayMs / 1_000);
      showOverlayMessage(
        `Данные авторизации истекли. Обновите мини-приложение через бота.
Повтор через ${retryDelaySec} сек.`,
        true,
        "Авторизация истекла",
      );
      windowRef.setTimeout(() => {
        if (!getCurrentUser?.() && !isInProgress()) {
          initAuth();
        }
      }, retryDelayMs);
      markAuthInProgress(false);
      return;
    }

    try {
      updateInitData(initData);
      const apiUrl = getApiUrl();
      const endpoint = `${apiUrl}/auth/telegram-init`;
      const payload = { initData };
      
      console.log("[AUTH] 📤 Sending auth request", {
        endpoint,
        apiUrl,
        initDataLength: initData.length,
        initDataPreview: initData.substring(0, 100) + "...",
        hasHash: initData.includes("hash="),
        hasAuthDate: initData.includes("auth_date="),
        ts: new Date().toISOString()
      });
      
      const response = await safeFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      console.log("[AUTH] 📥 Received auth response", {
        success: response.success,
        status: response.status,
        error: response.error,
        message: response.message,
        hasData: !!response.data,
        payload: response.payload,
        ts: new Date().toISOString()
      });
      
      // Обрабатываем ошибку access_denied из response (когда статус 403)
      if (!response.success && response.error === "access_denied") {
        // ВАЖНО: Помечаем авторизацию как завершенную, но БЕЗ пользователя
        // Это предотвратит дальнейшие попытки авторизации
        markAuthCompleted(true);
        markAuthInProgress(false);
        setAttempts(0); // Сбрасываем счетчик попыток
        
        // Устанавливаем флаг отказа в доступе - это заблокирует все дальнейшие API запросы
        setAccessDenied(true);
        
        // Очищаем пользователя, если он был установлен
        setCurrentUser?.(null);
        
        console.log("[AUTH] initAuth access_denied (from response) - blocking all API requests", {
          ts: new Date().toISOString(),
          message: response.message || "Доступ ограничен",
        });
        
        // ВАЖНО: Скрываем интерфейс приложения при отказе в доступе
        const appContainer = windowRef.document?.getElementById("main-app");
        if (appContainer) {
          appContainer.style.display = "none";
          appContainer.style.visibility = "hidden";
        }
        
        // Показываем overlay с сообщением об отказе
        showOverlay();
        showOverlayMessage(
          response.message ||
            "Доступ ограничен. Свяжитесь с командой, чтобы получить доступ.",
          false,
          "Доступ ограничен",
        );
        
        // Блокируем дальнейшие попытки авторизации
        // Не вызываем initAuth() снова
        return;
      }
      
      if (!response.success) {
        throw new Error(response.error || "Ошибка авторизации");
      }

      const resultData = response.data;
      if (resultData.success) {
        setCurrentUser?.(resultData.user);
        markAuthCompleted(true);
        markAuthInProgress(false);
        setAttempts(0);
        
        // Сохраняем initData в кэш, чтобы не проверять его повторно
        const currentInitData = selectCurrentInitData();
        if (currentInitData) {
          lastInitDataCheck = currentInitData;
          lastInitDataCheckTime = Date.now();
        }
        
        console.log("[AUTH] initAuth success", {
          userId: resultData.user?.id,
          ts: new Date().toISOString(),
        });
        // Скрываем overlay несколько раз для надежности (особенно важно для Android/Windows)
        hideOverlay();
        // Показываем основное приложение сразу
        const appContainer = windowRef.document?.getElementById("main-app");
        if (appContainer) {
          appContainer.style.display = "block";
          appContainer.style.visibility = "visible";
        }
        windowRef.setTimeout(() => {
          hideOverlay();
          if (appContainer) {
            appContainer.style.display = "block";
            appContainer.style.visibility = "visible";
          }
        }, 100);
        windowRef.setTimeout(() => {
          hideOverlay();
        }, 300);
        windowRef.setTimeout(() => {
          hideOverlay();
          if (appContainer) {
            appContainer.style.display = "block";
            appContainer.style.visibility = "visible";
          }
        }, 500);
        windowRef.setTimeout(() => {
          hideOverlay();
        }, 1000);
        onAuthSuccess(resultData.user);
      } else if (resultData.error === "access_denied") {
        markAuthCompleted(true);
        markAuthInProgress(false);
        console.log("[AUTH] initAuth access_denied", {
          ts: new Date().toISOString(),
          message:
            resultData.message ||
            "Приложение находится в разработке. Доступ ограничен для тестировщиков и разработчиков.",
        });
        
        // ВАЖНО: Скрываем интерфейс приложения при отказе в доступе
        const appContainer = windowRef.document?.getElementById("main-app");
        if (appContainer) {
          appContainer.style.display = "none";
          appContainer.style.visibility = "hidden";
        }
        
        // Показываем overlay с сообщением об отказе
        showOverlay();
        showOverlayMessage(
          resultData.message ||
            "Доступ ограничен. Свяжитесь с командой, чтобы получить доступ.",
          false,
          "Доступ ограничен",
        );
      } else {
        console.error(
          "❌ Ошибка авторизации:",
          resultData.error,
          resultData.message,
        );
        console.log("[AUTH] initAuth failure", {
          error: resultData.error,
          message: resultData.message,
          ts: new Date().toISOString(),
        });
        markAuthInProgress(false);
        setAttempts(getAttempts() + 1);
        const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * getAttempts());
        const retryDelaySec = Math.round(retryDelayMs / 1_000);
        showOverlayMessage(
          "Ошибка авторизации: " +
            (resultData.message || resultData.error || "Неизвестная ошибка") +
            `
Повтор через ${retryDelaySec} сек.`,
          true,
          "Ошибка авторизации",
        );
        windowRef.setTimeout(() => {
          if (!getCurrentUser?.() && !isInProgress()) {
            initAuth();
          }
        }, retryDelayMs);
      }
    } catch (error) {
      console.error("❌ Ошибка при запросе авторизации:", error);
      console.log("[AUTH] initAuth exception", {
        error: error?.message,
        ts: new Date().toISOString(),
      });
      setAttempts(getAttempts() + 1);
      const retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, 3_000 * getAttempts());
      const retryDelaySec = Math.round(retryDelayMs / 1_000);
      showOverlayMessage(
        "Ошибка подключения к серверу: " +
          (error.message || "Ошибка сети") +
          `. Повтор через ${retryDelaySec} сек.`,
        true,
        "Ошибка подключения",
      );
      windowRef.setTimeout(() => {
        if (!getCurrentUser?.() && !isInProgress()) {
          initAuth();
        }
      }, retryDelayMs);
    } finally {
      if (isCompleted()) {
        console.log("[AUTH] initAuth finally keep completed", {
          ts: new Date().toISOString(),
        });
      } else {
        markAuthInProgress(false);
        console.log("[AUTH] initAuth finally reset", {
          ts: new Date().toISOString(),
        });
      }
    }
  }

  function scheduleAuthRetry() {
    try {
      if (isCompleted() || isInProgress()) {
        return;
      }
      
      // Проверяем доступность Telegram WebApp
      const telegram = getTelegramApp();
      const webApp = windowRef.Telegram?.WebApp;
      
      if (!telegram && !webApp) {
        if (timers.retryTimeoutId) {
          windowRef.clearTimeout(timers.retryTimeoutId);
        }
        timers.retryTimeoutId = windowRef.setTimeout(() => {
          try {
            scheduleAuthRetry();
          } catch (error) {
            console.error("[AUTH ERROR] Error in scheduleAuthRetry retry:", error);
          }
        }, 500);
        return;
      }
      
      // Пробуем получить initData из разных источников
      let initData = null;
      try {
        initData = 
          webApp?.initData || 
          telegram?.initData || 
          selectCurrentInitData();
      } catch (error) {
        console.error("[AUTH ERROR] Error getting initData:", error);
      }
      
      console.log("[AUTH] scheduleAuthRetry check", {
        hasTelegram: !!telegram,
        hasWebApp: !!webApp,
        hasInitData: !!initData,
        initDataLength: initData?.length || 0,
        initDataType: typeof initData,
        hasHash: initData?.includes("hash=") || false,
        hasAuthDate: initData?.includes("auth_date=") || false,
        ts: new Date().toISOString()
      });
      
      if (
        typeof initData === "string" &&
        initData.length >= 50 &&
        initData.includes("hash=") &&
        initData.includes("auth_date=")
      ) {
        console.log("[AUTH] ✅ Valid initData found, calling initAuth()");
        try {
          initAuth();
        } catch (error) {
          console.error("[AUTH ERROR] Error in initAuth:", error);
          // Пробуем еще раз через некоторое время
          if (timers.retryTimeoutId) {
            windowRef.clearTimeout(timers.retryTimeoutId);
          }
          timers.retryTimeoutId = windowRef.setTimeout(() => {
            try {
              scheduleAuthRetry();
            } catch (retryError) {
              console.error("[AUTH ERROR] Error in scheduleAuthRetry after initAuth error:", retryError);
            }
          }, 2000);
        }
      } else if (!isCompleted()) {
        // Если initData не найден, но авторизация еще не завершена, пробуем еще раз
        // Но не показываем overlay сразу - даем время на инициализацию
        if (timers.retryTimeoutId) {
          windowRef.clearTimeout(timers.retryTimeoutId);
        }
        // Увеличиваем задержку для Android/Windows, где инициализация может быть медленнее
        const retryDelay = 1000; // 1 секунда вместо 500ms
        timers.retryTimeoutId = windowRef.setTimeout(() => {
          try {
            scheduleAuthRetry();
          } catch (error) {
            console.error("[AUTH ERROR] Error in scheduleAuthRetry retry:", error);
          }
        }, retryDelay);
      } else {
        // Если авторизация завершена, проверяем, что пользователь имеет доступ
        const currentUser = getCurrentUser?.();
        if (currentUser) {
          // Пользователь авторизован - показываем интерфейс
          hideOverlay();
          const appContainer = windowRef.document?.getElementById("main-app");
          if (appContainer) {
            appContainer.style.display = "block";
            appContainer.style.visibility = "visible";
          }
        } else {
          // Пользователь не авторизован - скрываем интерфейс
          const appContainer = windowRef.document?.getElementById("main-app");
          if (appContainer) {
            appContainer.style.display = "none";
            appContainer.style.visibility = "hidden";
          }
        }
      }
    } catch (error) {
      console.error("[AUTH ERROR] Critical error in scheduleAuthRetry:", error);
      // Пробуем еще раз через некоторое время даже при ошибке
      if (timers.retryTimeoutId) {
        windowRef.clearTimeout(timers.retryTimeoutId);
      }
      timers.retryTimeoutId = windowRef.setTimeout(() => {
        try {
          scheduleAuthRetry();
        } catch (retryError) {
          console.error("[AUTH ERROR] Error in scheduleAuthRetry after critical error:", retryError);
        }
      }, 2000);
    }
  }

  return {
    buildAuthHeaders,
    handleInvalidInitData,
    initAuth,
    scheduleAuthRetry,
    selectCurrentInitData,
    isAccessDenied,
    setAccessDenied,
  };
}

